import { type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { DURATION } from '@vello/tokens';
import { Mark, MarkOnDark } from './brand';
import { NavIcon, type NavIconName, UiIcon } from './icons';
import { Avatar } from './primitives';
import { Popover } from './overlays';
import { THEME_PREFERENCES, type ThemePreference, useColorScheme, useThemePreference } from '../theme-preference';
import { badgeCountText } from '../use-polled-counts';

/**
 * The navy sidebar's navigation (V-LIB-SHELL, D509; audit consoles F11 + admin #17). The brand row, the destination
 * list and the operator foot were rebuilt four times (pdv, merchant, fleet, agent) with `18px 12px 16px` padding that
 * no token owns, and the Admin carries a fifth, richer copy with nested groups, count badges and an icon rail. This is
 * the one component; it renders INSIDE the shell's sidebar slot:
 *
 *   <ConsoleShell sidebar={(close, { collapsed }) => (
 *     <SidebarNav label={t('nav.aria')} brand={{ title: t('chrome.brand') }} items={items} activeId={activeId}
 *       collapsed={collapsed} onSelect={(id) => { navigate(routeOf(id)); close(); }} operator={{ name, detail, signOutLabel, onSignOut }} />
 *   )} … />
 *
 *  - `items` is a tree (groups nest to any depth; the design draws three). A leaf is a button that calls `onSelect(id)`;
 *    a group is a disclosure button (`aria-expanded` + `aria-controls`). The active leaf carries `aria-current="page"`
 *    and its ancestors a quieter "contains the active page" state; navigating into a closed group opens its path.
 *  - `badge` is a count from a real server number (never decorative) with an optional spoken `label`; `dot` is the rail's
 *    alert dot.
 *  - `collapsed` draws the 68px icon rail: top-level tiles only (a group's tile goes to its first leaf, or stays on the
 *    active page when it already contains it), no labels, the brand reduced to the mark and the operator to the avatar.
 *  - Motion (A08 shell 1a, D547): a row nudges 2px right on hover and presses to .97; the active pill fades in over 360ms, and
 *    the row just picked flashes a .30 white wash for 40ms first (`data-flash`) — a click you can feel. All of it keeps
 *    playing under reduced motion (the design stills only its FX classes).
 *  - Keyboard: ArrowDown / ArrowUp / Home / End move between the visible rows, ArrowRight opens a group (and enters it),
 *    ArrowLeft closes it (or goes to the parent). Every row stays a tab stop — arrows are a shortcut, not a trap.
 * Every label is the caller's (translated) text; padding comes from `--sidebar-padding`.
 */
export interface SidebarNavItem {
  readonly id: string;
  readonly label: string;
  readonly icon: NavIconName;
  readonly children?: readonly SidebarNavItem[];
  /** A count from a real number, with the sentence a screen reader says ("3 pending"). */
  readonly badge?: { readonly count: number; readonly tone: 'danger' | 'warning'; readonly label?: string; /** The count is a lower bound: drawn `3+`. */ readonly atLeast?: boolean };
  /** The rail's alert dot — only for a real alert. */
  readonly dot?: boolean;
}

export interface SidebarNavOperator {
  readonly name: string;
  /** Second line (e-mail, role). */
  readonly detail?: string;
  /** Defaults to {@link initialsOf}(`name`). */
  readonly initials?: string;
  readonly signOutLabel: string;
  readonly onSignOut: () => void;
  /**
   * D798 / D803: the theme choice, in the operator's own menu - the identity (avatar, name, detail) is the button that opens it
   * (a console installed with a dark theme passes it): the menu's name and the three options' words, in the console's language.
   */
  readonly theme?: SidebarNavThemeLabels;
  /**
   * D806 (doc 35 §3.2): "Meu perfil" - the first item of the operator's menu, above the theme choice; it opens the console's own profile
   * page. A console that passes none keeps the menu as it was.
   */
  readonly profile?: { readonly label: string; readonly onOpen: () => void };
  /** D806: the operator's own photo (`GET /v1/me` `photoUrl`), drawn in place of the initials wherever the avatar is; absent → the initials. */
  readonly photoUrl?: string | undefined;
}

export interface SidebarNavThemeLabels {
  readonly label: string;
  readonly options: Readonly<Record<ThemePreference, string>>;
}

/**
 * The operator's own menu (D803, owner 2026-10-28: the loose theme icon beside "sign out" "ficou horrível"; clicking the operator is
 * where a person looks for their preferences). The identity - avatar, name, detail - is the button; the menu opens above it with the
 * theme choice (the current one checked). On the rail the avatar alone is the button and the menu opens beside the rail, fixed to the
 * viewport (the sidebar clips what overflows it). A console that passes no theme labels keeps the plain identity.
 * D806: "Meu perfil" (`operator.profile`) is the first item, above the theme heading; a console with a profile page and no theme labels
 * still gets the menu, with that one item.
 */
function AccountMenu({ operator, labels, rail }: { readonly operator: SidebarNavOperator; readonly labels: SidebarNavThemeLabels | undefined; readonly rail: boolean }): ReactElement {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ readonly left: number; readonly bottom: number }>();
  const [pref, setPref] = useThemePreference();
  const who = operator.detail !== undefined ? `${operator.name} · ${operator.detail}` : operator.name;
  const toggle = (e: MouseEvent<HTMLButtonElement>): void => {
    if (rail) {
      const r = e.currentTarget.getBoundingClientRect();
      setAt({ left: r.right + 8, bottom: window.innerHeight - r.bottom });
    }
    setOpen((o) => !o);
  };
  const place: CSSProperties = rail && at !== undefined ? { position: 'fixed', top: 'auto', left: at.left, bottom: at.bottom } : { top: 'auto', bottom: 'calc(100% + 8px)' };
  return (
    <span className="vello-sidebar__account-wrap" data-rail={rail}>
      <button type="button" className="vello-sidebar__account" aria-haspopup="menu" aria-expanded={open} aria-label={rail ? who : undefined} title={rail ? who : undefined} onClick={toggle}>
        <Avatar initials={operator.initials ?? initialsOf(operator.name)} size={rail ? 36 : 32} variant="chip" photoUrl={operator.photoUrl} />
        {!rail && (
          <span className="vello-sidebar-nav__operator">
            <span className="vello-sidebar__foot-name">{operator.name}</span>
            {operator.detail !== undefined && <span className="vello-sidebar__foot-mail">{operator.detail}</span>}
          </span>
        )}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} width={220} align="left" role="menu" ariaLabel={labels?.label ?? operator.profile?.label ?? operator.name} style={place}>
        {operator.profile !== undefined && (
          <button
            type="button"
            role="menuitem"
            className="vello-menu__item"
            onClick={() => {
              setOpen(false);
              operator.profile?.onOpen();
            }}
          >
            <UiIcon name="user" size={15} />
            {operator.profile.label}
          </button>
        )}
        {labels !== undefined && <div className="vello-menu__heading">{labels.label}</div>}
        {labels !== undefined && THEME_PREFERENCES.map((p) => (
          <button
            key={p}
            type="button"
            role="menuitemradio"
            aria-checked={pref === p}
            className="vello-menu__item"
            onClick={() => {
              setPref(p);
              setOpen(false);
            }}
          >
            {labels.options[p]}
            {pref === p && (
              <span className="vello-menu__check" aria-hidden="true">
                <UiIcon name="check" size={14} />
              </span>
            )}
          </button>
        ))}
      </Popover>
    </span>
  );
}

export interface SidebarNavProps {
  readonly items: readonly SidebarNavItem[];
  /** The active LEAF's id. */
  readonly activeId?: string;
  readonly onSelect: (id: string) => void;
  /** The `<nav>`'s accessible name ("Store sections"). */
  readonly label: string;
  /** Brand row: the mark and a title. The active scope lives in the header's scope switcher only (owner, 2026-10-12). */
  readonly brand?: { readonly title: string };
  /** Draws the icon rail (see above). */
  readonly collapsed?: boolean;
  readonly operator?: SidebarNavOperator;
  /** Group ids open on first render; the active leaf's ancestors are always opened. */
  readonly defaultExpandedIds?: readonly string[];
}

/**
 * Initials of a person or account name: the first letter of the first + last word, else the first two letters; `fallback` when
 * there is none. A bracketed qualifier ("Pousada Teste (balcão)") is not part of the name and a word's leading punctuation is
 * never an initial ("P(" for that name was D845 LT-008), so "Pousada Teste (balcão)" reads "PT". A name made only of a bracketed
 * word still uses it.
 */
export function initialsOf(name: string, fallback = '·'): string {
  const wordsOf = (text: string): string[] =>
    text
      .split(/\s+/)
      .map((w) => w.replace(/^[^\p{L}\p{N}]+/u, ''))
      .filter((w) => w !== '');
  const unbracketed = wordsOf(name.replace(/\([^)]*\)?/g, ' '));
  const parts = unbracketed.length > 0 ? unbracketed : wordsOf(name);
  const chars = (w: string): string[] => Array.from(w);
  const first = chars(parts[0] ?? '');
  const last = chars(parts[parts.length - 1] ?? '');
  const letters = parts.length >= 2 ? `${first[0] ?? ''}${last[0] ?? ''}` : first.slice(0, 2).join('');
  return letters.toLocaleUpperCase() || fallback;
}

/** The ids of the groups that lead to `targetId` (empty when it is top level or absent). */
function ancestorsOf(items: readonly SidebarNavItem[], targetId: string | undefined): readonly string[] {
  const walk = (list: readonly SidebarNavItem[]): string[] | undefined => {
    for (const item of list) {
      if (item.id === targetId) return [];
      const sub = item.children !== undefined ? walk(item.children) : undefined;
      if (sub !== undefined) return [item.id, ...sub];
    }
    return undefined;
  };
  return targetId === undefined ? [] : (walk(items) ?? []);
}

function containsId(item: SidebarNavItem, id: string | undefined): boolean {
  return id !== undefined && (item.id === id || (item.children ?? []).some((c) => containsId(c, id)));
}

function firstLeafId(item: SidebarNavItem): string {
  return item.children !== undefined && item.children.length > 0 ? firstLeafId(item.children[0] as SidebarNavItem) : item.id;
}

const ICON_BY_DEPTH = [17, 15, 14] as const;

export function SidebarNav({ items, activeId, onSelect, label, brand, collapsed = false, operator, defaultExpandedIds = [] }: SidebarNavProps): ReactElement {
  const baseId = useId();
  const scheme = useColorScheme();
  const navRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set([...defaultExpandedIds, ...ancestorsOf(items, activeId)]));

  // Navigating into a closed group opens its path, without closing what the person opened.
  useEffect(() => {
    const path = ancestorsOf(items, activeId);
    if (path.length === 0) return;
    setExpanded((prev) => (path.every((id) => prev.has(id)) ? prev : new Set([...prev, ...path])));
  }, [items, activeId]);

  // The click flash: the row picked becomes active carrying `data-flash` for DURATION.flash (40ms), then the CSS fades it into the pill.
  const [flashId, setFlashId] = useState<string | undefined>(undefined);
  const flashTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      if (flashTimer.current !== undefined) window.clearTimeout(flashTimer.current);
    },
    [],
  );
  const pick = (id: string): void => {
    if (id !== activeId) {
      if (flashTimer.current !== undefined) window.clearTimeout(flashTimer.current);
      setFlashId(id);
      flashTimer.current = window.setTimeout(() => setFlashId(undefined), DURATION.flash);
    }
    onSelect(id);
  };

  const toggle = (id: string, open?: boolean): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      const shouldOpen = open ?? !next.has(id);
      if (shouldOpen) next.add(id);
      else next.delete(id);
      return next;
    });

  const rows = (): HTMLButtonElement[] => Array.from(navRef.current?.querySelectorAll<HTMLButtonElement>('button[data-nav-row]') ?? []);
  const focusRow = (el: HTMLElement | null | undefined): void => el?.focus();

  const onKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    const target = (e.target as Element).closest<HTMLButtonElement>('button[data-nav-row]');
    if (target === null) return;
    const all = rows();
    const i = all.indexOf(target);
    const isGroup = target.dataset.navGroup === 'true';
    const open = target.getAttribute('aria-expanded') === 'true';
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(all[Math.min(i + 1, all.length - 1)]);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(all[Math.max(i - 1, 0)]);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(all[0]);
        break;
      case 'End':
        e.preventDefault();
        focusRow(all[all.length - 1]);
        break;
      case 'ArrowRight':
        if (!isGroup) return;
        e.preventDefault();
        if (!open) toggle(target.dataset.navId as string, true);
        else focusRow(all[i + 1]);
        break;
      case 'ArrowLeft': {
        if (isGroup && open) {
          e.preventDefault();
          toggle(target.dataset.navId as string, false);
          return;
        }
        const parent = target.dataset.navParent;
        if (parent !== undefined && parent !== '') {
          e.preventDefault();
          focusRow(all.find((b) => b.dataset.navId === parent));
        }
        break;
      }
      default:
    }
  };

  const brandRow =
    brand === undefined ? null : (
      <div className="vello-sidebar__brand">
        {/* D798: the rail is navy in light and snow in dark - the mark follows (brand rule: dark surface → white). */}
        {scheme === 'dark' ? <Mark size={collapsed ? 28 : 26} title={brand.title} /> : <MarkOnDark size={collapsed ? 28 : 26} title={brand.title} />}
        {!collapsed && <span className="vello-sidebar-nav__brand-title">{brand.title}</span>}
      </div>
    );

  const foot =
    operator === undefined ? null : collapsed ? (
      <div className="vello-sidebar-nav__foot-rail">
        {operator.theme !== undefined || operator.profile !== undefined ? (
          <AccountMenu operator={operator} labels={operator.theme} rail />
        ) : (
          <Avatar initials={operator.initials ?? initialsOf(operator.name)} size={36} variant="chip" photoUrl={operator.photoUrl} title={operator.detail !== undefined ? `${operator.name} · ${operator.detail}` : operator.name} />
        )}
      </div>
    ) : (
      <div className="vello-sidebar__foot">
        {operator.theme !== undefined || operator.profile !== undefined ? (
          <AccountMenu operator={operator} labels={operator.theme} rail={false} />
        ) : (
          <>
            <Avatar initials={operator.initials ?? initialsOf(operator.name)} size={32} variant="chip" photoUrl={operator.photoUrl} />
            <div className="vello-sidebar-nav__operator">
              <div className="vello-sidebar__foot-name">{operator.name}</div>
              {operator.detail !== undefined && <div className="vello-sidebar__foot-mail">{operator.detail}</div>}
            </div>
          </>
        )}
        <button type="button" className="vello-sidebar__signout" onClick={operator.onSignOut} aria-label={operator.signOutLabel} title={operator.signOutLabel}>
          <UiIcon name="logOut" size={14} />
        </button>
      </div>
    );

  const renderList = (list: readonly SidebarNavItem[], depth: number, parent: string): ReactElement => (
    <ul className="vello-sidebar-nav__list">
      {list.map((item) => {
        const isGroup = item.children !== undefined && item.children.length > 0;
        const open = isGroup && expanded.has(item.id);
        const active = item.id === activeId;
        const holdsActive = isGroup && containsId(item, activeId);
        const cls = `vello-nav-item${active ? ' is-active' : holdsActive ? ' contains-active' : ''}`;
        const childrenId = `${baseId}-${item.id}`;
        return (
          <li key={item.id}>
            <button
              type="button"
              className={cls}
              data-depth={Math.min(depth, 2)}
              data-nav-row=""
              data-nav-id={item.id}
              data-nav-parent={parent}
              data-nav-group={isGroup ? 'true' : undefined}
              data-flash={active && flashId === item.id ? '' : undefined}
              aria-expanded={isGroup ? open : undefined}
              aria-controls={isGroup && open ? childrenId : undefined}
              aria-current={active ? 'page' : undefined}
              onClick={() => (isGroup ? toggle(item.id) : pick(item.id))}
            >
              <NavIcon name={item.icon} size={ICON_BY_DEPTH[Math.min(depth, 2)] as number} />
              <span className="vello-nav-item__label">{item.label}</span>
              {item.badge !== undefined && (
                <span className={`vello-nav-badge tnum vello-nav-badge--${item.badge.tone}`}>
                  {badgeCountText(item.badge.count, item.badge.atLeast === true)}
                  {item.badge.label !== undefined && <span className="vello-sr-only"> {item.badge.label}</span>}
                </span>
              )}
              {isGroup && (
                <span className="vello-nav-item__chevron" data-open={open}>
                  <UiIcon name="chevronDown" size={13} />
                </span>
              )}
            </button>
            {isGroup && open && <div id={childrenId}>{renderList(item.children as readonly SidebarNavItem[], depth + 1, item.id)}</div>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="vello-sidebar-nav" data-collapsed={collapsed ? 'true' : undefined}>
      {brandRow}
      <nav ref={navRef} className="vello-sidebar-nav__nav" aria-label={label} onKeyDown={onKeyDown}>
        {collapsed ? (
          <ul className="vello-sidebar-nav__list vello-sidebar-nav__list--rail">
            {items.map((item) => {
              const holdsActive = containsId(item, activeId);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`vello-rail-item${holdsActive ? ' is-active' : ''}`}
                    data-nav-row=""
                    data-nav-id={item.id}
                    title={item.label}
                    aria-label={item.label}
                    aria-current={holdsActive ? 'page' : undefined}
                    onClick={() => onSelect(holdsActive && activeId !== undefined ? activeId : firstLeafId(item))}
                  >
                    <NavIcon name={item.icon} size={20} />
                    {item.dot === true && <span className="vello-rail-item__dot" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          renderList(items, 0, '')
        )}
      </nav>
      <div className="vello-sidebar-nav__spacer" />
      {foot}
    </div>
  );
}
